import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from "hooks";

// material-ui
import {useTheme} from '@mui/material/styles';
import {
    Drawer,
    Fab,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Slider,
    Tooltip,
    Typography
} from '@mui/material';
import {IconSettings} from '@tabler/icons-react';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import SubCard from 'ui-components/cards/SubCard';
import AnimateButton from 'ui-components/extended/AnimateButton';
import {
    setBorderRadius as setBorderRadiusAction,
    saveCustomization
} from 'store/customizationReducer';
import { gridSpacing } from 'store/constant';

// concat 'px'
function valueText(value) {
    return `${value}px`;
}

// ==============================|| LIVE CUSTOMIZATION ||============================== //

const Customization = ({visible = true}) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const customization = useAppSelector((state) => state.customization);
    const { fontFamily } = customization;
    const themeColor = customization.theme;

    // drawer on/off
    const [open, setOpen] = useState(false);

    const handleToggle = () => {
        setOpen(!open);
    };

    const [borderRadius, setBorderRadius] = useState(customization.borderRadius);
    useEffect(() => {
        dispatch(setBorderRadiusAction(borderRadius));
    }, [borderRadius]);

    const handleBorderRadius = (event, newValue) => {
        // console.log(event);
        dispatch(saveCustomization({ borderRadius: newValue }));
    };

    const handleTheme = (value) => {
        dispatch(saveCustomization({ theme: value }));
    }

    const handleFontFamily = (value) => {
        dispatch(saveCustomization({ fontFamily: value }));
    }

    return (
        <>
            {/* toggle button */}
            {visible && (
                <Tooltip title="Live Customize">
                <Fab
                    component="div"
                    onClick={handleToggle}
                    size="medium"
                    variant="circular"
                    color="secondary"
                    sx={{
                        borderRadius: 0,
                        borderTopLeftRadius: '50%',
                        borderBottomLeftRadius: '50%',
                        borderTopRightRadius: '50%',
                        borderBottomRightRadius: '4px',
                        top: '25%',
                        position: 'fixed',
                        right: 10,
                        zIndex: theme.zIndex.speedDial
                    }}
                >
                    <AnimateButton type="rotate">
                        <IconButton color="inherit" size="large" disableRipple>
                            <IconSettings/>
                        </IconButton>
                    </AnimateButton>
                </Fab>
            </Tooltip>
                )}

            <Drawer
                anchor="right"
                onClose={handleToggle}
                open={open}
                PaperProps={{
                    sx: {
                        width: 280
                    }
                }}
            >
                <PerfectScrollbar component="div">
                    <Grid container spacing={gridSpacing} sx={{p: 3}}>
                        <Grid size={{xs: 12}}>
                            {/* font family */}
                            <SubCard title="Theme">
                                <FormControl>
                                    <RadioGroup
                                        aria-label="theme"
                                        value={themeColor}
                                        onChange={(e) => handleTheme(e.target.value)}
                                        name="row-radio-buttons-group"
                                    >
                                        <FormControlLabel
                                            value="light"
                                            control={<Radio/>}
                                            label="Light"
                                            sx={{
                                                '& .MuiSvgIcon-root': {fontSize: 28},
                                                '& .MuiFormControlLabel-label': {color: theme.palette.grey[900]}
                                            }}
                                        />
                                        <FormControlLabel
                                            value="dark"
                                            control={<Radio/>}
                                            label="Dark"
                                            sx={{
                                                '& .MuiSvgIcon-root': {fontSize: 28},
                                                '& .MuiFormControlLabel-label': {color: theme.palette.grey[900]}
                                            }}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </SubCard>
                        </Grid>
                        <Grid size={{xs: 12}}>
                            {/* font family */}
                            <SubCard title="Font Family">
                                <FormControl>
                                    <RadioGroup
                                        aria-label="font-family"
                                        value={fontFamily}
                                        onChange={(e) => handleFontFamily(e.target.value)}
                                        name="row-radio-buttons-group"
                                    >
                                        <FormControlLabel
                                            value="'Roboto', sans-serif"
                                            control={<Radio/>}
                                            label="Roboto"
                                            sx={{
                                                '& .MuiSvgIcon-root': {fontSize: 28},
                                                '& .MuiFormControlLabel-label': {color: theme.palette.grey[900]}
                                            }}
                                        />
                                        <FormControlLabel
                                            value="'Poppins', sans-serif"
                                            control={<Radio/>}
                                            label="Poppins"
                                            sx={{
                                                '& .MuiSvgIcon-root': {fontSize: 28},
                                                '& .MuiFormControlLabel-label': {color: theme.palette.grey[900]}
                                            }}
                                        />
                                        <FormControlLabel
                                            value="'Inter', sans-serif"
                                            control={<Radio/>}
                                            label="Inter"
                                            sx={{
                                                '& .MuiSvgIcon-root': {fontSize: 28},
                                                '& .MuiFormControlLabel-label': {color: theme.palette.grey[900]}
                                            }}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </SubCard>
                        </Grid>
                        <Grid size={{xs: 12}}>
                            {/* border radius */}
                            <SubCard title="Border Radius">
                                <Grid size={{xs: 12}} container spacing={2} alignItems="center" sx={{mt: 2.5}}>
                                    <Grid>
                                        <Typography variant="h6" color="secondary">
                                            4px
                                        </Typography>
                                    </Grid>
                                    <Grid sx={{ flex: 1 }}>
                                        <Slider
                                            size="small"
                                            value={borderRadius}
                                            onChangeCommitted={handleBorderRadius}
                                            onChange={(event, value) => setBorderRadius(value)}
                                            getAriaValueText={valueText}
                                            valueLabelDisplay="on"
                                            aria-labelledby="discrete-slider-small-steps"
                                            marks
                                            step={2}
                                            min={4}
                                            max={24}
                                            color="secondary"
                                            sx={{
                                                '& .MuiSlider-valueLabel': {
                                                    color: 'secondary.light'
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6" color="secondary">
                                            24px
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </SubCard>
                        </Grid>
                    </Grid>
                </PerfectScrollbar>
            </Drawer>
        </>
    );
};

export default Customization;
